// ─── RescueBanner ────────────────────────────────────────────
// Soft top-bar nudge shown in the dashboard when the morning reveal
// is scheduled within the next 24 hours and hasn't been sent yet.
// Goal: gently remind the planner there's still time to gather a few more
// moments — without pressure UI. Tapping the link jumps to the Collected tab.

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function RescueBanner({ eventId, token, eventDate, onJumpToCollected }) {
  const [hoursLeft, setHoursLeft] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(`sitoa_rescue_dismissed_${eventId}`) === '1'; }
    catch { return false; }
  });

  useEffect(() => {
    if (!eventId || !eventDate || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/events/${eventId}/reveal-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.data || {};
        if (data.is_sent) return; // already gone out
        const scheduledTime = data.scheduled_time || '10:30';
        // Reveal moment = day AFTER the wedding at scheduled time
        const wedding = new Date(eventDate);
        if (Number.isNaN(wedding.getTime())) return;
        const [hh, mm] = String(scheduledTime).split(':').map(Number);
        const moment = new Date(wedding);
        moment.setDate(moment.getDate() + 1);
        moment.setHours(Number.isFinite(hh) ? hh : 10, Number.isFinite(mm) ? mm : 30, 0, 0);
        const diffMs = moment.getTime() - Date.now();
        const diffH = diffMs / 36e5;
        if (cancelled) return;
        if (diffH > 0 && diffH <= 24) {
          setHoursLeft(Math.max(1, Math.round(diffH)));
        }
      } catch { /* */ }
    })();
    return () => { cancelled = true; };
  }, [eventId, eventDate, token]);

  if (dismissed || hoursLeft == null) return null;

  const handleDismiss = () => {
    try { sessionStorage.setItem(`sitoa_rescue_dismissed_${eventId}`, '1'); } catch { /* */ }
    setDismissed(true);
  };

  const word = hoursLeft === 1 ? 'hour' : 'hours';

  return (
    <div
      className="rounded-xl border px-4 py-3 mb-4 flex items-center gap-3 backdrop-blur-sm"
      style={{
        background: 'linear-gradient(90deg, rgba(124,58,237,0.10) 0%, rgba(217,70,239,0.06) 100%)',
        borderColor: 'rgba(124,58,237,0.25)',
      }}
      data-testid="rescue-banner"
    >
      <Sparkles className="h-4 w-4 flex-shrink-0" style={{ color: '#a78bfa' }} />
      <p className="text-sm font-light flex-1 leading-relaxed" style={{ color: '#a78bfa' }}>
        Reveal goes out in {hoursLeft} {word} ·{' '}
        <button
          onClick={onJumpToCollected}
          className="font-medium hover:underline underline-offset-4"
          style={{ color: '#c4b5fd' }}
          data-testid="rescue-banner-cta"
        >
          Still time for a few more moments &rarr;
        </button>
      </p>
      <button
        onClick={handleDismiss}
        className="opacity-50 hover:opacity-90 transition-opacity flex-shrink-0"
        data-testid="rescue-banner-dismiss"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" style={{ color: '#a78bfa' }} />
      </button>
    </div>
  );
}
